import React, { useEffect, useMemo, useRef, useState } from "react";
import { Building2, Layers, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../shadcn/ui/dialog";
import toastUtils from "../../../../utils/toast";
import { showApiMessage } from "../../../../utils/messageHandler";
import { buildingAddressService } from "../../../../services/buildingAddressService";
import HSimpleFloorPlan from "../../../../hcomponents/HSimpleFloorPlan";
import { getCurrentUserOrganizationId } from "../../host-clients/utils/hostClientsUtils";

const emptyFloor = () => ({
  id: undefined,
  floorNumber: 0,
  label: "",
  remove: false,
  floorPlan: { layout: [] },
});

const ManageFloorsDialog = ({ buildingAddressId, triggerButton, onUpdated, isReadOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [floors, setFloors] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorIndex, setEditorIndex] = useState(null);
  const [editorInitialLayout, setEditorInitialLayout] = useState([]);
  const [removingFloor, setRemovingFloor] = useState(null);
  const floorPlanRef = useRef(null);
  const currentOrgId = getCurrentUserOrganizationId();

  const canOpen = useMemo(() => !!buildingAddressId, [buildingAddressId]);

  useEffect(() => {
    if (!isOpen || !buildingAddressId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await buildingAddressService.getBuildingAddressById(buildingAddressId);
        if (cancelled) return;
        if (resp.status === 200 && resp.data) {
          const fetchedFloors = Array.isArray(resp.data.floors) ? resp.data.floors : [];
          setFloors(
            fetchedFloors.map(f => ({
              id: f.id,
              floorNumber: f.floorNumber ?? 0,
              label: f.label ?? "",
              remove: !!f.remove,
              organizationId: f.organizationId,
              floorPlan:
                f.floorPlan && Array.isArray(f.floorPlan.layout) ? { layout: f.floorPlan.layout } : { layout: [] },
            }))
          );
        } else {
          showApiMessage(resp, "load building address floors");
        }
      } catch (e) {
        showApiMessage(e, "load building address floors");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, buildingAddressId]);

  const addFloor = () => {
    if (isReadOnly) return;
    setFloors(prev => [...prev, emptyFloor()]);
  };

  const removeFloorAt = async index => {
    if (isReadOnly) return;
    const floor = floors[index];
    if (floor?.id) {
      setRemovingFloor(floor.id);
      try {
        const resp = await buildingAddressService.deleteFloor(buildingAddressId, floor.id);
        if (resp.status === 204) {
          setFloors(prev => prev.filter((_, i) => i !== index));
        } else {
          showApiMessage(resp, "delete floor");
        }
      } catch (e) {
        showApiMessage(e, "delete floor");
      } finally {
        setRemovingFloor(null);
      }
    } else {
      setFloors(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateFloorField = (index, field, value) => {
    setFloors(prev => prev.map((f, i) => (i === index ? { ...f, [field]: value } : f)));
  };

  const openFloorPlanEditor = index => {
    if (isReadOnly) return;
    const floor = floors[index] || emptyFloor();
    const layout = Array.isArray(floor.floorPlan?.layout) ? floor.floorPlan.layout : [];
    setEditorIndex(index);
    setEditorInitialLayout(layout);
    setEditorOpen(true);
  };

  const handleEditorSave = () => {
    try {
      if (!floorPlanRef.current || typeof floorPlanRef.current.getSaveData !== "function") {
        toastUtils.error("Error saving layout", {
          description: "Editor not ready.",
        });
        return;
      }
      const data = floorPlanRef.current.getSaveData();
      const newLayout = Array.isArray(data) ? data : Array.isArray(data?.layout) ? data.layout : [];
      if (!Array.isArray(newLayout)) {
        toastUtils.error("Invalid layout data", {
          description: "Could not read layout.",
        });
        return;
      }
      setFloors(prev => prev.map((f, i) => (i === editorIndex ? { ...f, floorPlan: { layout: newLayout } } : f)));
      setEditorOpen(false);
    } catch (e) {
      toastUtils.error("Error", { description: "Failed to save floor plan." });
    }
  };

  const handleSave = async () => {
    if (!buildingAddressId) return;
    try {
      setSaving(true);

      const payload = {
        floors: floors
          .filter(f => !f.id)
          .map(f => ({
            floorNumber: Number(f.floorNumber) || 0,
            label: f.label,
            floorPlan: {
              layout: Array.isArray(f.floorPlan?.layout) ? f.floorPlan.layout : [],
            },
          })),
      };

      const resp = await buildingAddressService.patchBuildingAddress(buildingAddressId, payload);
      showApiMessage(resp, "update building floors");
      if (resp.status >= 200 && resp.status < 300) {
        if (onUpdated) onUpdated(resp.data);
        setIsOpen(false);
      }
    } catch (e) {
      showApiMessage(e, "update building floors");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={open => setIsOpen(canOpen ? open : false)}>
        <DialogTrigger asChild>
          {triggerButton || (
            <Button variant="outline" size="sm" type="button" disabled={!canOpen || isReadOnly}>
              <Layers className="w-4 h-4 mr-2" />
              Manage Floors
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Manage Floors & Floor Plans
            </DialogTitle>
            <DialogDescription>
              Add, edit, or remove floors for this building. Use the floor plan editor to design layouts visually.
            </DialogDescription>
          </DialogHeader>

          {!buildingAddressId && <div className="text-sm text-red-600">Select a building address first.</div>}

          {buildingAddressId && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">Building ID: {buildingAddressId}</div>
                <Button variant="outline" size="sm" onClick={addFloor} disabled={isReadOnly} type="button">
                  <Plus className="w-4 h-4 mr-2" /> Add Floor
                </Button>
              </div>

              {loading ? (
                <div className="text-sm text-gray-500">Loading floors...</div>
              ) : floors.length === 0 ? (
                <div className="text-sm text-gray-500">No floors yet. Add one to begin.</div>
              ) : (
                <div className="space-y-4">
                  {floors.map((floor, index) => {
                    const isFloorLocked = floor.id != null && (!currentOrgId || floor.organizationId !== currentOrgId);
                    return (
                      <div
                        key={index}
                        className={`border rounded-md p-4 space-y-3 ${isFloorLocked ? "opacity-60" : ""}`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                          <div className="md:col-span-2">
                            <Label htmlFor={`floor-number-${index}`}>Floor Number</Label>
                            <Input
                              id={`floor-number-${index}`}
                              type="number"
                              disabled={isReadOnly || isFloorLocked}
                              value={floor.floorNumber}
                              onChange={e => updateFloorField(index, "floorNumber", e.target.value)}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <Label htmlFor={`floor-label-${index}`}>Label</Label>
                            <Input
                              id={`floor-label-${index}`}
                              disabled={isReadOnly || isFloorLocked}
                              value={floor.label}
                              onChange={e => updateFloorField(index, "label", e.target.value)}
                              placeholder="e.g., Ground, Mezzanine, 5th"
                            />
                          </div>
                          <div className="md:col-span-1 flex items-end justify-end">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => removeFloorAt(index)}
                              disabled={isReadOnly || isFloorLocked || removingFloor === floor.id}
                              type="button"
                              aria-label="remove-floor"
                            >
                              {removingFloor === floor.id ? (
                                <span className="w-4 h-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-600">
                            {isFloorLocked ? (
                              <span className="text-amber-600">Locked — belongs to another organization</span>
                            ) : Array.isArray(floor.floorPlan?.layout) && floor.floorPlan.layout.length > 0 ? (
                              `${floor.floorPlan.layout.length} elements in layout`
                            ) : (
                              "No layout yet"
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            onClick={() => openFloorPlanEditor(index)}
                            disabled={isReadOnly || isFloorLocked}
                          >
                            Edit Floor Plan
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsOpen(false)} type="button">
                  Cancel
                </Button>
                <Button variant="spaciadefault" onClick={handleSave} disabled={isReadOnly || saving} type="button">
                  {saving ? "Saving..." : "Save Floors"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Nested floor plan editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="sm:max-w-[960px] w-full max-h-[90vh] overflow-hidden p-0">
          <div className="flex flex-col h-[80vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <DialogTitle>Edit Floor Plan</DialogTitle>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditorOpen(false)} type="button">
                  Cancel
                </Button>
                <Button variant="spaciadefault" onClick={handleEditorSave} type="button">
                  Save
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <HSimpleFloorPlan
                ref={floorPlanRef}
                initialLayout={editorInitialLayout}
                onSave={() => {}}
                className="w-full h-full"
                useIcons={true}
                useColors={false}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageFloorsDialog;
